import { dateStringFromDate } from '../../../common/src/date_string.ts';
import { Household, Org } from '../../../common/src/model.ts';
import { isSponsoringOrg } from '../../../common/src/subscription_banner_policy.ts';
import { deriveTrialState } from '../../../common/src/trial_state.ts';

// What tapping the profile drawer's Subscription row should do:
// - none: not tappable, no chevron
// - chooseTrialOrSubscribe: the subscription gate (Pages.SubscriptionTrial), which offers a free trial pre-trial
// - subscribe: the same gate, which is subscribe-only (no fresh trial offer) during/after the trial
// - manage: manageSubscription() (Stripe billing portal / Apple subscription management)
export type SubscriptionAction = 'none' | 'chooseTrialOrSubscribe' | 'subscribe' | 'manage';

export interface SubscriptionStatus {
  label: string;
  action: SubscriptionAction;
}

// Status for the Subscription row in the profile drawer.
// Deliberately independent of the active user (GitHub issue #3399): subscription state belongs to the
// household and its org - admin/staff access rides on the server-computed household.isDiscounted, and a
// member's internal flag plays no part. Pure function of household + org (+ now) so it can be unit tested.
export function getSubscriptionStatus({
  household,
  org,
  now = Date.now(),
}: {
  household: Pick<Household, 'subscriptionExpirationTimestamp' | 'trialStartDate' | 'isDiscounted'> | undefined;
  org: Pick<Org, 'areHouseholdsSubscribed' | 'isUsageBased' | 'notInterested'> | undefined;
  now?: number;
}): SubscriptionStatus {
  // the household's church pays for its households (Legacy household add-on or Usage-Based)
  if (isSponsoringOrg(org)) {
    return { label: 'Church Sponsored', action: 'none' };
  }

  // full library access without a subscription (manual flag or staff/admin in a legacy org)
  if (household?.isDiscounted) {
    return { label: 'Discounted', action: 'none' };
  }

  // a paid (or Stripe-trialing) subscription sets subscriptionExpirationTimestamp; it wins over the
  // in-app trial. The timestamp is the current period end, i.e. the renewal date - "Renews" tells the
  // user when their card is charged (a cancel-at-period-end also reads Renews until access lapses;
  // the client cannot distinguish it)
  const expiration = household?.subscriptionExpirationTimestamp;
  if (expiration && expiration > now) {
    return { label: `Renews ${formatDate(new Date(expiration))}`, action: 'manage' };
  }

  // the in-app trial is trialStartDate + trialLengthInDays and never sets subscriptionExpirationTimestamp,
  // so an active trial is distinguishable from a paid subscription
  const trialState = deriveTrialState({
    trialStartDate: household?.trialStartDate,
    clientDate: dateStringFromDate(new Date(now)),
  });
  switch (trialState.status) {
    case 'active':
      return {
        label: trialState.daysLeft === 1 ? '1 day left' : `${trialState.daysLeft} days left`,
        action: 'subscribe',
      };
    case 'expired':
      return { label: 'Trial Expired', action: 'subscribe' };
    case 'pre-trial':
      return { label: 'Free', action: 'chooseTrialOrSubscribe' };
  }
}

// no year - renewals are at most a year out, and "Renews Oct 2" reads cleaner
function formatDate(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
